import _ from "lodash";
import { useEffect, useRef, useMemo } from "react";
import Router from "next/router";
import { Modal } from "antd";

export const useWarnIfUnsavedChanges = (unsavedChanges, message = true) => {
  useEffect(() => {
    const routeChangeStart = (url) => {
      if (Router.asPath !== url && unsavedChanges && !window.confirm(message)) {
        throw new Error("Abort route change. Please ignore this error.");
      }
    };

    const beforeunload = (e) => {
      if (unsavedChanges) {
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", beforeunload);
    Router.events.on("routeChangeStart", routeChangeStart);

    return () => {
      window.removeEventListener("beforeunload", beforeunload);
      Router.events.off("routeChangeStart", routeChangeStart);
    };
  }, [unsavedChanges]);
};

export const useOnClickOutside = (ref, handler) => {
  useEffect(
    () => {
      const listener = (event) => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }
        handler(event);
      };
      document.addEventListener("mousedown", listener);
      document.addEventListener("touchstart", listener);
      return () => {
        document.removeEventListener("mousedown", listener);
        document.removeEventListener("touchstart", listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler]
  );
};

export const usePostCheckedEffect = (props) => {
  useEffect(() => {
    const action = () => {
      props.createPost
        ? props.createPost(props.token, {
            ...props.data,
            visibility: "prohibited_word_review",
          })
        : props.updatePost(props.token, props.postId, {
            ...props.data,
            visibility: "prohibited_word_review",
          });
    };
    const handlePostCheckedTime = () => {
      if (props.postCheckedTime > 1) {
        Modal.confirm({
          content: <div>检测到内容包含敏感词，是否申请人工审核</div>,
          cancelText: "取消",
          okText: "确定",
          onOk: () => action(),
        });
      }
    };

    handlePostCheckedTime();
  }, [props.postCheckedTime]);

  useEffect(() => {
    return () => {
      props.setPostCheckedTime(0);
    };
  }, []);
};

export function useThrottleFn(fn, delay = 300) {
  const fnRef = useRef(fn);
  fnRef.current = fn; // always latest fn

  const throttled = useRef(
    throttle((...args) => fnRef.current(...args), delay)
  ).current;

  useEffect(() => {
    return () => throttled.cancel?.();
  }, []);

  return throttled;
}

export function useDebounceFn(fn, delay = 300) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const debounced = useRef(
    _.debounce(function (...args) {
      fnRef.current(...args);
    }, delay)
  ).current;

  useEffect(() => {
    return () => debounced.cancel?.();
  }, []);

  return debounced;
}

export function useDraftCacheKey({ type, postId, draftId }) {
  return useMemo(() => {
    return `draft_${type}_${postId}_${draftId}`;
  }, [type, postId, draftId]);
}
